import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, cx } from '@/components/primitives';
import { useDismissableMenu } from './useDismissableMenu';

export interface SignInDestination {
  /** Which side of the marketplace this row belongs to. */
  audience: 'seeker' | 'employer';
  /** The question a visitor can answer about themselves without knowing our words. */
  question: string;
  label: string;
  hint: string;
  to: string;
  /** The audience colour, as a left edge. */
  edge: string;
}

/**
 * Two doors, asked as a question.
 *
 * The header used to offer one "Sign in" for a marketplace with two entirely
 * different populations behind it, and the login page then asked which one you
 * were after you had already arrived. Asking here instead costs a click that
 * was being spent anyway, and it lets the answer ride on the URL so the login
 * page never asks twice.
 *
 * Deliberately not four buttons. Candidate, employee, employer and recruiter
 * in one row makes every visitor read all four and guess; a recruiter is an
 * employer with a narrower role, not a fifth door, so the hint says so.
 */
export const SIGN_IN_DESTINATIONS: readonly SignInDestination[] = [
  {
    audience: 'seeker',
    question: 'Looking for work?',
    label: 'Job seeker sign in',
    hint: 'One code to your mobile. Apply and track applications.',
    to: '/login?role=seeker',
    edge: 'border-l-brand-600',
  },
  {
    audience: 'employer',
    question: 'Hiring workers?',
    label: 'Employer sign in',
    hint: 'For approved companies. Recruiters and hiring managers sign in here too.',
    to: '/login?role=employer',
    edge: 'border-l-action-600',
  },
] as const;

/**
 * The header's sign-in control.
 *
 * Opens on click, like `NavMenu` and for the same reasons: a hover menu is
 * unreachable by keyboard, and on a touch device the first tap becomes a hover
 * that swallows the second.
 */
export function SignInMenu({ className }: { className?: string }) {
  const { open, setOpen, wrapper, id } = useDismissableMenu();
  const { t } = useTranslation();

  return (
    <div ref={wrapper} className={cx('relative', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className={cx(
          'flex min-h-11 items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium',
          'ring-1 ring-inset ring-ink-500 transition-colors hover:bg-ink-50',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          open ? 'text-ink-900' : 'text-ink-800',
        )}
      >
        {t('nav.signIn', 'Sign in')}
        <Icon
          name="chevronDown"
          size="xs"
          className={cx('transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          id={id}
          className="absolute right-0 top-full z-30 mt-1 w-80 rounded-lg bg-white p-1.5 shadow-lg ring-1 ring-ink-200 motion-safe:animate-toast-in"
        >
          {SIGN_IN_DESTINATIONS.map((destination) => (
            <div key={destination.audience}>
              <p className="px-3 pb-1 pt-2 text-xs text-ink-500">{destination.question}</p>
              <Link
                to={destination.to}
                className={cx(
                  'block rounded-md border-l-[3px] px-3 py-2 hover:bg-ink-50',
                  'focus-visible:outline focus-visible:outline-2',
                  destination.edge,
                )}
              >
                <span className="block text-sm font-semibold text-ink-900">
                  {destination.label}
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-ink-500">
                  {destination.hint}
                </span>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
